/**
 * The web need ranking pass, as a hook.
 *
 * Lifted out of the old `WebNeedPanel` for the same reason as `useSyncStatus`:
 * the three band counts are now the list's band filter, the freshness date and
 * the start button are in the data menu, and all three read the same progress
 * object. Polling it twice would let one part of the screen disagree with
 * another about how many companies are worth calling.
 *
 * The freshness date matters as much as the counts. A stored ranking with no
 * date next to it is a number nobody can decide whether to trust, and this one
 * goes out of date every time the register or the site scanner learns something.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  getWebNeedProgress,
  startWebNeedPass,
  stopWebNeedPass,
  type WebNeedProgress,
} from "../../../../lib/webNeed";

/** Polled only while a pass is running. Idle costs one request per visit. */
const POLL_MS = 3_000;

export interface WebNeedPassState {
  progress: WebNeedProgress | null;
  busy: boolean;
  start: () => Promise<void>;
  stop: () => Promise<void>;
}

/** `onDone` fires once, when a pass this page was watching finishes. */
export function useWebNeedPass(onDone?: () => void): WebNeedPassState {
  const [progress, setProgress] = useState<WebNeedProgress | null>(null);
  const [busy, setBusy] = useState(false);
  const wasRunning = useRef(false);

  useEffect(() => {
    let stopped = false;
    let timer: number | undefined;

    const tick = async () => {
      try {
        const next = await getWebNeedProgress();
        if (stopped) return;
        setProgress(next);
        // A finished pass has reordered the list behind this page. Reloading it
        // is better than leaving the operator looking at yesterday's ranking
        // under a date claiming today's.
        if (wasRunning.current && !next.running) onDone?.();
        wasRunning.current = next.running;
        if (next.running) timer = window.setTimeout(tick, POLL_MS);
      } catch {
        // A failed poll is not worth a message. The next visit asks again.
      }
    };

    void tick();
    return () => {
      stopped = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [onDone]);

  const run = useCallback(async (begin: boolean) => {
    setBusy(true);
    try {
      await (begin ? startWebNeedPass() : stopWebNeedPass());
      const next = await getWebNeedProgress();
      setProgress(next);
      wasRunning.current = next.running;
      if (next.running) {
        window.setTimeout(() => void getWebNeedProgress().then(setProgress).catch(() => {}), POLL_MS);
      }
    } catch {
      // The next poll corrects whatever this missed.
    } finally {
      setBusy(false);
    }
  }, []);

  const start = useCallback(() => run(true), [run]);
  const stop = useCallback(() => run(false), [run]);

  return { progress, busy, start, stop };
}
